import React from 'react'

import { Row, Col, Select, TimePicker, InputNumber, Input, Checkbox, Button, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

const { Title, Text, Paragraph } = Typography

const dayOptions = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
]

export default function (props) {
  const cp = props.calendarProps
  const slots = cp.interviewSlots || []
  const slotErrors = cp.slotErrors || []

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
      {/* Availability slots */}
      <div style={{ marginBottom: 32 }}>
        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Availability:</Title>
        <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
          Set the days and times you are available for interviews. Add multiple rows for the same day to have separate time windows.
        </Paragraph>
        <div style={{ background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0', padding: 12 }}>
          {slots.map((slot, index) => {
            const rowError = slotErrors[index]
            return (
              <div
                key={index}
                style={{
                  padding: '6px 0',
                  borderBottom: index < slots.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ width: 120 }}>
                    {index === 0 && (
                      <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Day</Text>
                    )}
                    <Select
                      size="small"
                      value={slot.day}
                      onChange={(v) => cp.updateSlot(index, 'day', v)}
                      options={dayOptions}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    {index === 0 && (
                      <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Start</Text>
                    )}
                    <TimePicker
                      size="small"
                      use12Hours
                      format="h:mm A"
                      minuteStep={5}
                      allowClear={false}
                      status={rowError ? 'error' : undefined}
                      style={{ width: 120 }}
                      value={slot.startTime ? dayjs(slot.startTime, 'h:mm A') : null}
                      onChange={(t) => cp.updateSlot(index, 'startTime', t ? t.format('h:mm A') : '')}
                    />
                  </div>
                  <div>
                    {index === 0 && (
                      <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>End</Text>
                    )}
                    <TimePicker
                      size="small"
                      use12Hours
                      format="h:mm A"
                      minuteStep={5}
                      allowClear={false}
                      status={rowError ? 'error' : undefined}
                      style={{ width: 120 }}
                      value={slot.endTime ? dayjs(slot.endTime, 'h:mm A') : null}
                      onChange={(t) => cp.updateSlot(index, 'endTime', t ? t.format('h:mm A') : '')}
                    />
                  </div>
                  <div style={{ paddingTop: index === 0 ? 20 : 0 }}>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => cp.removeSlot(index)}
                    />
                  </div>
                </div>
                {rowError && (
                  <Text type="danger" style={{ display: 'block', marginTop: 4, marginLeft: 128, fontSize: 12 }}>
                    {rowError}
                  </Text>
                )}
              </div>
            )
          })}
          <Button size="small" type="dashed" onClick={cp.addSlot} style={{ width: '100%', marginTop: 8 }}>
            + Add Slot
          </Button>
        </div>
      </div>

      {/* Interview duration + candidates per slot */}
      <div style={{ marginBottom: 32 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>Interview Duration</Title>
            <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}>
              Set the number of minutes for your interview.
            </Paragraph>
            <div style={{ display: 'flex', gap: 8 }}>
              <InputNumber
                value={cp.hrDuration}
                onChange={(v) => cp.handleOnChange('hrDuration')(null, v ?? 0)}
                min={0}
                max={8}
                style={{ flex: 1 }}
                addonAfter="hrs"
              />
              <InputNumber
                value={cp.minDuration}
                onChange={(v) => cp.handleOnChange('minDuration')(null, v ?? 0)}
                min={0}
                max={60}
                step={5}
                style={{ flex: 1 }}
                addonAfter="mins"
              />
            </div>
            {cp.hrDuration === 0 && cp.minDuration === 0 ? (
              <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                *Interview duration needs to be at least 5 minutes.
              </Text>
            ) : null}
          </Col>
          <Col span={12}>
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>Candidates per slot</Title>
            <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}>
              Max candidates per time slot.
            </Paragraph>
            <InputNumber
              value={cp.maxPerInterviewSlot}
              onChange={(v) => cp.handleOnChange('maxPerInterviewSlot')(null, String(v ?? ''))}
              min={1}
              max={100}
              style={{ width: '100%' }}
            />
            {cp.maxPerInterviewSlot < 1 ? (
              <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                *minimum of one candidate.
              </Text>
            ) : null}
          </Col>
        </Row>
      </div>

      {/* Scheduling type */}
      <div style={{ marginBottom: 32 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text style={{ fontSize: 13, color: '#546e7a' }}>Scheduling Type</Text>
            <Select
              value={cp.scheduleSequence}
              onChange={(v) => cp.handleOnChange('scheduleSequence', true)(null, null, v)}
              style={{ width: '100%', marginTop: 4 }}
              options={[
                { value: 'random', label: 'Random (Individuals with varying schedules)' },
                { value: 'next-available', label: 'Next Available (Immediate interview times)' },
              ]}
            />
          </Col>
        </Row>
      </div>

      {/* Buffer / lead time */}
      <div style={{ marginBottom: 32 }}>
        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Candidates cannot schedule interviews in:</Title>
        <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
          Prevents booking within a set amount of time from now.
        </Paragraph>
        <Row gutter={16}>
          <Col span={12}>
            <InputNumber
              value={cp.noInterviewsWithinNumber}
              onChange={(v) => cp.handleOnChange('noInterviewsWithinNumber')(null, String(v ?? 0))}
              min={0}
              max={100}
              style={{ width: '100%' }}
              placeholder="1"
            />
          </Col>
          <Col span={12}>
            <Select
              value={cp.noInterviewsWithinPeriod}
              onChange={(v) => cp.handleOnChange('noInterviewsWithinPeriod', true)(null, null, v)}
              style={{ width: '100%' }}
              options={[
                { value: 'd', label: 'Days' },
                { value: 'h', label: 'Hours' },
                { value: 'm', label: 'Minutes' },
              ]}
            />
          </Col>
        </Row>
      </div>

      {/* Include store location */}
      <div style={{ marginBottom: 32 }}>
        <Checkbox
          checked={cp.includeStoreLocation}
          onChange={(e) => cp.handleIncludeStoreLocationCheck()(null, e.target.checked)}
        >
          <span style={{ fontWeight: 600, fontSize: 15 }}>Include store location on email</span>
        </Checkbox>
        <Paragraph type="secondary" style={{ marginTop: 4, marginLeft: 24, fontSize: 13 }}>
          Check this only if you are performing on-site interviews. The physical address of your location will be
          included in the notification email.
        </Paragraph>
      </div>

      {/* Additional email content */}
      <div style={{ marginBottom: 32 }}>
        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Additional email content:</Title>
        <Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 12, fontSize: 13 }}>
          Include a customized interview confirmation email.
        </Paragraph>
        <Input.TextArea
          value={cp.confirmationMsg}
          onChange={(e) => cp.handleOnChange('confirmationMsg')(null, e.target.value)}
          rows={3}
          placeholder="Additional Email content"
        />
      </div>
    </div>
  )
}
